import ExitToApp from "@mui/icons-material/ExitToApp";
import { Box } from "@mui/material";
import _ from "lodash";
import { ReactElement, useMemo } from "react";
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import { FlexBox } from "../FlexBox";
import Table, { TableBox, TableControls } from "../Table";

type TableRow = {
  id: string;
  name: string;
  link: string | null;
  smsSegments: number;
  smsMessages: number;
  mmsMessages: number;
  smsUnregisteredSegments: number;
  smsUnregisteredMessages: number;
  mmsUnregisteredMessages: number;
  smsShortcodeSegments: number;
  smsShortcodeMessages: number;
  mmsShortcodeMessages: number;
  donationCount: number;
  recurringDonationCount: number;
  amountRaisedInCents: number;
  hasActBlueIntegration: boolean;
};

export const USAGE_SORTS = [
  {
    key: "name_asc",
    label: "Name, A-Z",
  },
  {
    key: "name_desc",
    label: "Name, Z-A",
  },
  {
    key: "sms_segments_desc",
    label: "SMS Segments, Most to Least",
  },
  {
    key: "sms_segments_asc",
    label: "SMS Segments, Least to Most",
  },
  {
    key: "sms_messages_desc",
    label: "SMS Messages, Most to Least",
  },
  {
    key: "sms_messages_asc",
    label: "SMS Messages, Least to Most",
  },
  {
    key: "mms_messages_desc",
    label: "MMS Messages, Most to Least",
  },
  {
    key: "mms_messages_asc",
    label: "MMS Messages, Least to Most",
  },
  {
    key: "donation_count_desc",
    label: "All Donations, Most to Least",
  },
  {
    key: "donation_count_asc",
    label: "All Donations, Least to Most",
  },
  {
    key: "recurring_donation_count_desc",
    label: "Recurring Donations, Most to Least",
  },
  {
    key: "recurring_donation_count_asc",
    label: "Recurring Donations, Least to Most",
  },
  {
    key: "amount_raised_in_cents_desc",
    label: "Amount Raised, Most to Least",
  },
  {
    key: "amount_raised_in_cents_asc",
    label: "Amount Raised, Least to Most",
  },
] as const;

export type UsageSort = (typeof USAGE_SORTS)[number]["key"];

function validateSort(sort: string): UsageSort {
  return USAGE_SORTS.find(s => s.key === sort)?.key ?? USAGE_SORTS[0].key;
}

function renderRegisteredAndUnregistered(
  registered: number,
  unregistered: number,
  shortcode: number
) {
  if (unregistered === 0 && shortcode === 0) {
    return registered.toLocaleString();
  }

  return (
    <FlexBox direction="column">
      <Box>
        {registered > 0 ? `${registered.toLocaleString()} registered` : ""}
      </Box>
      <Box>
        {unregistered > 0
          ? `${unregistered.toLocaleString()} unregistered`
          : ""}
      </Box>
      <Box>
        {shortcode > 0 ? `${shortcode.toLocaleString()} shortcode` : ""}
      </Box>
    </FlexBox>
  );
}

function formatDataForExport({
  csvExportFileName,
  nameTitle,
  showFundraisingFields,
  filteredRows,
}: {
  csvExportFileName: string;
  nameTitle: string;
  showFundraisingFields: boolean;
  filteredRows: TableRow[];
}): {
  data: Record<string, any>[];
  fileName: string;
  options: Papa.UnparseConfig;
} {
  const includeUnregistered = filteredRows.some(
    row =>
      row.smsUnregisteredSegments > 0 ||
      row.smsUnregisteredMessages > 0 ||
      row.mmsUnregisteredMessages > 0
  );

  const includeShortcode = filteredRows.some(
    row =>
      row.smsShortcodeSegments > 0 ||
      row.smsShortcodeMessages > 0 ||
      row.mmsShortcodeMessages > 0
  );

  const onlyRegistered = !includeUnregistered && !includeShortcode;
  const registeredPrefix = onlyRegistered ? "" : "Registered ";

  const columns = [
    nameTitle,
    `${registeredPrefix}SMS Segments`,
    `${registeredPrefix}SMS Messages`,
    `${registeredPrefix}MMS Messages`,
    ...(includeUnregistered
      ? [
          "Unregistered SMS Segments",
          "Unregistered SMS Messages",
          "Unregistered MMS Messages",
        ]
      : []),
    ...(includeShortcode
      ? [
          "Shortcode SMS Segments",
          "Shortcode SMS Messages",
          "Shortcode MMS Messages",
        ]
      : []),
    ...(showFundraisingFields
      ? ["All Donations", "Recurring Donations", "Amount Raised"]
      : []),
    "URL",
  ];

  return {
    fileName: csvExportFileName,
    options: {
      columns,
    },
    data: filteredRows.map(row => ({
      [nameTitle]: row.name,
      [`${registeredPrefix}SMS Segments`]: row.smsSegments,
      [`${registeredPrefix}SMS Messages`]: row.smsMessages,
      [`${registeredPrefix}MMS Messages`]: row.mmsMessages,
      ...(includeUnregistered
        ? {
            "Unregistered SMS Segments": row.smsUnregisteredSegments,
            "Unregistered SMS Messages": row.smsUnregisteredMessages,
            "Unregistered MMS Messages": row.mmsUnregisteredMessages,
          }
        : {}),
      ...(includeShortcode
        ? {
            "Shortcode SMS Segments": row.smsShortcodeSegments,
            "Shortcode SMS Messages": row.smsShortcodeMessages,
            "Shortcode MMS Messages": row.mmsShortcodeMessages,
          }
        : {}),
      ...(showFundraisingFields
        ? {
            "All Donations": row.donationCount,
            "Recurring Donations": row.recurringDonationCount,
            "Amount Raised": (row.amountRaisedInCents / 100).toFixed(2),
          }
        : {}),
      URL: row.link
        ? `${document.location.protocol}//${document.location.host}${row.link}`
        : "",
    })),
  };
}

export default function UsageBreakdownTable({
  rows,
  nameTitle,
  searchTitle,
  csvExportFileName,
}: {
  rows: TableRow[];
  nameTitle: string;
  searchTitle: string;
  csvExportFileName: string;
}): ReactElement {
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, USAGE_SORTS[0].key)
  );

  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );

  const validatedSort = validateSort(sort);

  const filteredRows: TableRow[] = useMemo(() => {
    // filter
    const searchedRows = search
      ? rows.filter(
          ({
            name,
            smsSegments,
            smsMessages,
            mmsMessages,
            smsUnregisteredSegments,
            smsUnregisteredMessages,
            mmsUnregisteredMessages,
            smsShortcodeSegments,
            smsShortcodeMessages,
            mmsShortcodeMessages,
            donationCount,
            recurringDonationCount,
            amountRaisedInCents,
          }) => {
            const normalizedFilter = search.trim().toLocaleLowerCase();

            if (
              [
                smsSegments,
                smsMessages,
                mmsMessages,
                smsUnregisteredSegments,
                smsUnregisteredMessages,
                mmsUnregisteredMessages,
                smsShortcodeSegments,
                smsShortcodeMessages,
                mmsShortcodeMessages,
                donationCount,
                recurringDonationCount,
                amountRaisedInCents,
              ]
                .map(String)
                .includes(normalizedFilter)
            ) {
              return true;
            }

            if (name.toLocaleLowerCase().includes(normalizedFilter)) {
              return true;
            }

            return false;
          }
        )
      : rows.slice(0);

    // sort
    return searchedRows.sort((a, b) => {
      switch (validatedSort) {
        case "sms_segments_asc":
          return (
            a.smsSegments +
            a.smsUnregisteredSegments +
            a.smsShortcodeSegments -
            (b.smsSegments + b.smsUnregisteredSegments + b.smsShortcodeSegments)
          );
        case "sms_segments_desc":
          return (
            b.smsSegments +
            b.smsUnregisteredSegments +
            b.smsShortcodeSegments -
            (a.smsSegments + a.smsUnregisteredSegments + a.smsShortcodeSegments)
          );
        case "sms_messages_asc":
          return (
            a.smsMessages +
            a.smsUnregisteredMessages +
            a.smsShortcodeMessages -
            (b.smsMessages + b.smsUnregisteredMessages + b.smsShortcodeMessages)
          );
        case "sms_messages_desc":
          return (
            b.smsMessages +
            b.smsUnregisteredMessages +
            b.smsShortcodeMessages -
            (a.smsMessages + a.smsUnregisteredMessages + a.smsShortcodeMessages)
          );
        case "mms_messages_asc":
          return (
            a.mmsMessages +
            a.mmsUnregisteredMessages +
            a.mmsShortcodeMessages -
            (b.mmsMessages + b.mmsUnregisteredMessages + b.mmsShortcodeMessages)
          );
        case "mms_messages_desc":
          return (
            b.mmsMessages +
            b.mmsUnregisteredMessages +
            b.mmsShortcodeMessages -
            (a.mmsMessages + a.mmsUnregisteredMessages + a.mmsShortcodeMessages)
          );
        case "donation_count_asc":
          return a.donationCount - b.donationCount;
        case "donation_count_desc":
          return b.donationCount - a.donationCount;
        case "recurring_donation_count_asc":
          return a.recurringDonationCount - b.recurringDonationCount;
        case "recurring_donation_count_desc":
          return b.recurringDonationCount - a.recurringDonationCount;
        case "amount_raised_in_cents_asc":
          return a.amountRaisedInCents - b.amountRaisedInCents;
        case "amount_raised_in_cents_desc":
          return b.amountRaisedInCents - a.amountRaisedInCents;
        case "name_asc":
          return (a.name || "").localeCompare(b.name || "");
        case "name_desc":
          return (b.name || "").localeCompare(a.name || "");
      }
    });
  }, [rows, validatedSort, search]);

  const pageRows = filteredRows.slice(page * pageSize, (page + 1) * pageSize);

  const showFundraisingFields = rows.some(row => row.hasActBlueIntegration);

  return (
    <>
      <TableControls
        sort={{
          value: sort,
          onChange: value => {
            setSort(value as UsageSort);
            setPage(0);
          },
          options: USAGE_SORTS.map(sort => ({
            value: sort.key,
            label: sort.label,
          })),
        }}
        search={{
          value: search,
          onChange: value => {
            setSearch(value as string);
            setPage(0);
          },
          placeholder: searchTitle,
        }}
        exportButtons={[
          {
            id: "csv",
            label: "Download CSV",
            getData: () =>
              Promise.resolve(
                formatDataForExport({
                  csvExportFileName,
                  nameTitle,
                  showFundraisingFields,
                  filteredRows,
                })
              ),
          },
        ]}
      />
      <TableBox sx={{ pb: 2 }}>
        <Table
          columns={[
            {
              key: "name",
              title: nameTitle,
              renderValue: row => row.name,
            },
            {
              key: "smsSegments",
              title: "SMS Segments",
              renderValue: row =>
                renderRegisteredAndUnregistered(
                  row.smsSegments,
                  row.smsUnregisteredSegments,
                  row.smsShortcodeSegments
                ),
            },
            {
              key: "smsMessages",
              title: "SMS Messages",
              renderValue: row =>
                renderRegisteredAndUnregistered(
                  row.smsMessages,
                  row.smsUnregisteredMessages,
                  row.smsShortcodeMessages
                ),
            },
            {
              key: "mmsMessages",
              title: "MMS Messages",
              renderValue: row =>
                renderRegisteredAndUnregistered(
                  row.mmsMessages,
                  row.mmsUnregisteredMessages,
                  row.mmsShortcodeMessages
                ),
            },
            ...(showFundraisingFields
              ? [
                  {
                    key: "donationCount",
                    title: "All Donations",
                    renderValue: (row: TableRow) =>
                      row.hasActBlueIntegration
                        ? row.donationCount.toLocaleString()
                        : "-",
                  },
                  {
                    key: "recurringDonationCount",
                    title: "Recurring Donations",
                    renderValue: (row: TableRow) =>
                      row.hasActBlueIntegration
                        ? row.recurringDonationCount.toLocaleString()
                        : "-",
                  },
                  {
                    key: "amountRaised",
                    title: "Amount Raised",
                    renderValue: (row: TableRow) =>
                      row.hasActBlueIntegration
                        ? (row.amountRaisedInCents / 100).toLocaleString(
                            "en-US",
                            {
                              style: "currency",
                              currency: "USD",
                              maximumFractionDigits: 2,
                            }
                          )
                        : "-",
                  },
                ]
              : []),
          ]}
          rows={pageRows}
          rowActions={row =>
            row.link
              ? [
                  {
                    id: "link",
                    type: "link",
                    icon: <ExitToApp />,
                    tooltipLabel: `Go To ${nameTitle}`,
                    href: row.link,
                  },
                ]
              : []
          }
          emptyState="No results"
          totalCount={filteredRows.length}
          rowsPerPage={pageSize}
          onChangeRowsPerPage={newRowsPerPage => {
            setPageSize(newRowsPerPage);
            setPage(0);
          }}
          page={page}
          onChangePage={setPage}
        />
      </TableBox>
    </>
  );
}
